import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Modal } from "shared/components/Modal";
import { Select } from "shared/components/forms/Select";
import {
  CreditReportModels,
  CreditReportModelsNumbers,
  DataVendors
} from "shared/types";
import { formatToCurrency } from "shared/utils";
import { instance } from "api/apiBase";
import { CreditReportRerunOptions } from "types/landlord/reports";
import { getErrorMessage } from "utils/error";
import { logger } from "logger";

type FormatOptionLabel = NonNullable<
  ComponentProps<typeof Select>["formatOptionLabel"]
>;

const VENDOR_LABELS: Partial<Record<DataVendors, string>> = {
  [DataVendors.Equifax]: "Equifax",
  [DataVendors.TransUnion]: "TransUnion"
};

const MODEL_LABELS: Record<string, string> = {
  [CreditReportModelsNumbers[CreditReportModels.VantageScore]]:
    "VantageScore 4.0",
  [CreditReportModelsNumbers[CreditReportModels.FicoScore]]: "FICO 9",
  [CreditReportModelsNumbers[CreditReportModels.ResidentScore]]: "ResidentScore"
};

export const CreditReportRerunModal: FC<{
  open: boolean;
  onClose: () => void;
  applicantId: number;
  applicationId: number;
  verificationId: number;
  rerunOptions: CreditReportRerunOptions;
}> = ({
  open,
  onClose,
  applicantId,
  applicationId,
  verificationId,
  rerunOptions
}) => {
  const queryClient = useQueryClient();
  const [vendor, setVendor] = useState(rerunOptions.vendor);
  const [modelNumber, setModelNumber] = useState(rerunOptions.model_number);

  useEffect(() => {
    if (!open) return;
    setVendor(rerunOptions.vendor);
    setModelNumber(rerunOptions.model_number);
  }, [open, rerunOptions.vendor, rerunOptions.model_number]);

  const vendorOptions = useMemo(
    () =>
      rerunOptions.vendors.map((value) => ({
        value,
        label: VENDOR_LABELS[value] ?? value
      })),
    [rerunOptions.vendors]
  );

  const modelsForVendor = useMemo(
    () => rerunOptions.models.filter((model) => model.vendors.includes(vendor)),
    [rerunOptions.models, vendor]
  );

  const feeByModelNumber = useMemo(
    () =>
      new Map(
        rerunOptions.models.map((model) => [model.model_number, model.fee])
      ),
    [rerunOptions.models]
  );

  const modelOptions = useMemo(
    () =>
      modelsForVendor.map((model) => ({
        value: model.model_number,
        label: MODEL_LABELS[model.model_number] ?? model.model_number
      })),
    [modelsForVendor]
  );

  const isModelOffered = modelOptions.some(
    (option) => option.value === modelNumber
  );

  const formatModelOptionLabel = useCallback<FormatOptionLabel>(
    (option) => {
      const fee =
        "value" in option ? feeByModelNumber.get(String(option.value)) : null;
      return (
        <div className="flex items-center gap-2">
          <span>{option.label}</span>
          {fee ? (
            <span className="bg-content-1000 rounded-[4px] p-1 text-white text-xs">
              {formatToCurrency(fee)}
            </span>
          ) : null}
        </div>
      );
    },
    [feeByModelNumber]
  );

  const changeVendor = (nextVendor: DataVendors) => {
    setVendor(nextVendor);
    const nextModels = rerunOptions.models.filter((model) =>
      model.vendors.includes(nextVendor)
    );
    if (!nextModels.some((model) => model.model_number === modelNumber))
      setModelNumber(nextModels[0]?.model_number ?? "");
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/verifications/${verificationId}/rerun`,
        { vendor, model_number: modelNumber }
      ),
    onSuccess: () => {
      toast.success("Report successfully re-run");
      queryClient
        .refetchQueries({
          queryKey: ["APPLICATION", String(applicationId)]
        })
        .catch((error) => {
          logger.error("Failed to invalidate queries", error);
        });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(`Error re-running report: ${getErrorMessage(error)}`);
    }
  });

  return (
    <Modal
      opened={open}
      onClose={onClose}
      panelClassName="max-w-[510px] w-full"
    >
      <div className="p-6 space-y-2">
        <div className="flex justify-between items-center">
          <h1 className="text-xl text-content-1000 font-bold">Re-run report</h1>
          <IconButton
            onClick={onClose}
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.LG}
          />
        </div>
        <p className="text-base text-content-500">
          Keep the current selections to re-run the same report, or choose a
          different credit bureau or score model below
        </p>
      </div>
      <div className="bg-warning-50 px-6 py-4 flex gap-4 items-center border-y border-alpha-100">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          className="text-warning-500 size-6 shrink-0"
        />
        <p className="text-sm text-warning-700 font-medium">
          You will be charged for each re-run, even if your selections stay the
          same
        </p>
      </div>
      <div className="py-4 px-6 bg-background-900 space-y-4">
        <div>
          <Select
            id="credit-report-rerun-vendor"
            label="Credit bureau"
            hideOptional
            options={vendorOptions}
            value={vendor}
            isDisabled={!rerunOptions.can_change_vendor || isPending}
            onChange={(e) => changeVendor(e.target.value as DataVendors)}
          />
        </div>
        <div>
          <Select
            id="credit-report-rerun-model"
            label="Credit score requested"
            hideOptional
            options={modelOptions}
            value={modelNumber}
            isDisabled={isPending}
            onChange={(e) => setModelNumber(e.target.value)}
            formatOptionLabel={formatModelOptionLabel}
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end px-6 py-4 border-t border-alpha-100">
        <Button size={ButtonSize.SM} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          loading={isPending}
          disabled={isPending || !isModelOffered}
          onClick={() => mutate()}
        >
          Re-run report
        </Button>
      </div>
    </Modal>
  );
};
